import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import { MapPin, Star } from "lucide-react";
import { motion } from "framer-motion";

/**
 * @param {{
 *   hotel: {
 *     id: string;
 *     name: string;
 *     area: string;
 *     rating: number;
 *     price_per_night: number;
 *     image: string;
 *     amenities: string[];
 *     room_type: string;
 *   };
 *   onSelect: () => void;
 *   index: number;
 * }} props
 */
export default function AccommodationCard({ hotel, onSelect, index }) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);

  const visibleAmenities = hotel.amenities.slice(0, 3);
  const extraCount = hotel.amenities.length - 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="h-full"
    >
      <Card className="h-full flex flex-col overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer bg-white border border-gray-200 rounded-2xl">
        {/* Image */}
        <div className="relative h-48 shrink-0 overflow-hidden">
          <img
            src={hotel.image}
            alt={hotel.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          {/* Rating Badge */}
          <div className="absolute top-3 right-3">
            <div className="bg-[#D4AF37] text-[#0B2421] text-sm font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <Star className="h-3 w-3 fill-current" />
              <span>{hotel.rating}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col flex-1 space-y-3">
          <div>
            <h3 className="font-bold text-[#0B2421] text-sm leading-tight line-clamp-2 mb-1 min-h-[2.5rem]">
              {hotel.name}
            </h3>
            <div className="flex items-center gap-1 text-gray-500 text-sm">
              <MapPin className="h-3 w-3 text-[#D4AF37]" />
              <span>{hotel.area}</span>
            </div>
          </div>

          {/* Amenities Pills */}
          <div className="flex flex-wrap gap-1">
            {visibleAmenities.map((amenity) => (
              <span
                key={amenity}
                className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
              >
                {amenity}
              </span>
            ))}
            {extraCount > 0 && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                +{extraCount} more
              </span>
            )}
          </div>

          {/* Spacer pushes price + button to the bottom */}
          <div className="flex-1" />

          {/* Price */}
          <div className="flex items-end justify-between">
            <div>
              <span className="text-xs text-gray-400">From</span>
              <div className="text-xl font-bold text-[#0B2421]">
                SGD {hotel.price_per_night}
                <span className="text-sm font-normal text-gray-500"> /night</span>
              </div>
            </div>
          </div>

          <Button
            onClick={onSelect}
            className="w-full bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold"
          >
            View Details
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}
